import { BootErrorScreen } from '#~/routes/boot-overlay/boot-error-screen.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import type { BootError } from '#~/depot/types.js';
import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import { onboardingContextFor, useOnboardingTracker } from '../hooks/use-onboarding-tracker.js';

interface AutoLinkFailureStateProps {
  readonly onRetry: () => void;
}

export const AutoLinkFailureState = ({ onRetry }: AutoLinkFailureStateProps): ReactElement => {
  const tracker = useOnboardingTracker();
  const reportedRef = useRef(false);
  const error: BootError = {
    message: 'Unable to link your Bridge.',
    detail:
      "Auto Link couldn't connect this Bridge to your Studio. Try again, and check your internet connection if it keeps failing.",
    severity: 'retryable',
    retryFn: onRetry
  };

  useEffect(() => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    // The bridge lifecycle keeps the low-level IPC error. This row marks the
    // same failure as a terminal onboarding blocker.
    tracker.trackFailed({
      context: onboardingContextFor('detecting'),
      rawError: 'Bridge did not connect because AutoLink failed.',
      terminal: true,
      properties: { reason: 'auto_link_failed' }
    });
  }, [tracker]);

  // The failed prerequisite replaces the wizard instead of expiring its Bridge timer.
  return (
    <div className='fixed inset-0 z-100 bg-dark' {...DataSTag(Sel.OnboardingFailure, 'auto-link')}>
      <BootErrorScreen source='auto-link' error={error} selector={Sel.OnboardingFailure} />
    </div>
  );
};
